import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { FormikHandlers, useFormikContext } from 'formik';
import { Input, Select, Notification } from 'Components';
import {
	HIERARCHY_LABEL_GROUP_VALUES,
	getAllowedChildLabels,
	isLeafLabel
} from 'app-config';
import { getHierarchyLabel } from 'Services/Api/HierarchyLabel/HierarchyLabelService';
import { FIELD_PATTERN } from 'Helpers/validations';
import { Level } from '../../../components';
import { isGroupsHierarchyLimitReachedXLSX } from '../../../GroupsHierarchy.helpers';
import { ValuesXLSX } from '../../../GroupsHierarchy.types';
import { useGroupsHierarchyContext } from '../../../GroupsHierarchyContext';
import styles from './LevelItem.module.scss';

interface LevelItemInputsProps {
	name: string;
	isPropertyLevelDisabled: boolean;
	setLevelWithProperty(value: boolean, levelName: string): void;
}

const LevelItemInputs = ({
	name,
	isPropertyLevelDisabled,
	setLevelWithProperty
}: LevelItemInputsProps) => {
	const { t } = useTranslation();
	const {
		values,
		setFieldError,
		setFieldValue
	} = useFormikContext<ValuesXLSX>();
	const {
		groupsHierarchySizeLimit,
		amountOfGroupsInTenant,
		parent
	} = useGroupsHierarchyContext();

	// Determine the previous level's hierarchy label so the current level's
	// dropdown is filtered according to the hierarchy rules.
	// `name` is like "levels.0", "levels.1", etc.
	const levelIndex = Number(name.split('.')[1]);

	// Fetch the parent group's hierarchy label from the backend so the first
	// level's dropdown can be correctly filtered.
	const [parentHierarchyLabel, setParentHierarchyLabel] = useState<
		string | undefined
	>();
	const [parentLabelLoading, setParentLabelLoading] = useState<boolean>(
		!!parent?.id
	);
	useEffect(() => {
		if (parent?.id) {
			setParentLabelLoading(true);
			getHierarchyLabel(parent.id)
				.then((res) => {
					if (res.success && res.data) {
						setParentHierarchyLabel(res.data.node_type);
					}
				})
				.catch(() => {
					// Silently ignore – dropdown will show all options
				})
				.finally(() => {
					setParentLabelLoading(false);
				});
		}
	}, [parent?.id]);

	const prevLevelHierarchyLabel: string | undefined = useMemo(() => {
		if (levelIndex > 0) {
			return values.levels[levelIndex - 1]?.hierarchyLabel;
		}
		// First level – use the parent group's actual hierarchy label
		return parentHierarchyLabel;
	}, [levelIndex, values.levels, parentHierarchyLabel]);

	const allowedLabels = useMemo(
		() => getAllowedChildLabels(prevLevelHierarchyLabel),
		[prevLevelHierarchyLabel]
	);

	const isPrevLevelLeaf = isLeafLabel(prevLevelHierarchyLabel);

	// Watch for changes to the parent level's hierarchyLabel and clear this level's
	// label if it becomes invalid
	useEffect(() => {
		const currentLabel = values.levels[levelIndex]?.hierarchyLabel;
		if (currentLabel && !allowedLabels.includes(currentLabel as any)) {
			// Current selection is no longer valid under the new parent label
			setFieldValue(`${name}.hierarchyLabel`, undefined);
			setFieldValue(`${name}.isProperty`, false);
		}
	}, [
		prevLevelHierarchyLabel,
		allowedLabels,
		levelIndex,
		name,
		setFieldValue,
		values.levels
	]);

	const handleHierarchy: FormikHandlers['handleBlur'] = (
		ev: ChangeEvent<HTMLInputElement>
	) => {
		const { value } = ev.target;

		if (!FIELD_PATTERN.numeric.test(value)) return;

		if (
			isGroupsHierarchyLimitReachedXLSX(
				values,
				groupsHierarchySizeLimit,
				amountOfGroupsInTenant
			)
		) {
			Notification.error({
				description: t('groupsHierarchy.groupsLimitReached')
			});
			setFieldError(`${name}.numberOfGroups`, 'limit');
			return;
		}
	};

	const handleHierarchyLabelChange = (value: string) => {
		// When hierarchy label is set to "Property", automatically set isProperty to true
		const isProperty = value === 'Property';
		setFieldValue(`${name}.isProperty`, isProperty);

		// Update the level with property state
		setLevelWithProperty(isProperty, name);
	};

	return (
		<>
			<div className={cn(styles.levelItem, styles.levelItemName)}>
				<Input.FormikField
					name={`${name}.levelName`}
					label={t('groupsHierarchy.formXLSX.levelItem.name')}
				/>
			</div>
			<div className={styles.levelItem}>
				<Input.FormikField
					name={`${name}.numberOfGroups`}
					onBlur={handleHierarchy}
					maxLength={3}
					label={t(
						'groupsHierarchy.formXLSX.levelItem.numberOfGroups'
					)}
				/>
			</div>
			<div className={cn(styles.levelItem, styles.levelRadioButtons)}>
			<Select.FormikField
				label={t('groups.groupDetails.type')}
				name={`${name}.hierarchyLabel`}
					required
					disabled={parentLabelLoading || isPrevLevelLeaf}
					onChange={handleHierarchyLabelChange}
				>
					{allowedLabels.map((label) => (
						<Select.Option key={label} value={label}>
							{t(`groups.groupDetails.groupLevelType.${label}`)}
						</Select.Option>
					))}
				</Select.FormikField>
			</div>
		</>
	);
};

interface LevelItemProps extends LevelItemInputsProps {
	levelNumber: number;
}
const LevelItem = ({ levelNumber, ...levelItemInputProps }: LevelItemProps) => {
	return (
		<Level
			levelNumber={levelNumber}
			Component={<LevelItemInputs {...levelItemInputProps} />}
			withDashedLine={false}
		/>
	);
};

export default LevelItem;
